import "./index.scss";

import { PureComponent } from "react";
import PropTypes from "prop-types";
import { defineMessages, injectIntl } from "react-intl";

import compose from "util/compose";
import FormGroupErrors from "common/form/group_errors";
import Button from "common/core/button";
import FormRow from "common/form/elements/row";
import { StyledTextInput } from "common/form/inputs/text";
import TosV2 from "common/tos";
import { getStripeCardElementsUI } from "common/settings/payment/util";
import { PAYMENT_CARD, PAYMENT_ACH } from "constants/organization";
import { getFormValues } from "util/form";

import { verifyAch } from "../ach_util";

const messages = defineMessages({
  cardName: {
    id: "f0f8d2e6-bc4a-42b8-9527-9fa9a07c027e",
    defaultMessage: "Cardholder Name",
  },
  tosActionNew: {
    id: "d65fb63a-291e-4348-a73a-fb1f81489475",
    defaultMessage: "By changing your plan and clicking 'Submit Payment'",
  },
  delayedCharge: {
    id: "f8ae7489-5408-4507-a609-f61412125386",
    defaultMessage: "Charge will not be made until end of payment period",
  },
  submit: {
    id: "3b23efc2-9623-42b8-8a1a-23cea809e8e3",
    defaultMessage: "Submit Payment",
  },
  connectBank: {
    id: "e3004fd8-9b09-4273-a720-34598ab6b8a5",
    defaultMessage: "Connect Bank Account",
  },
  changePayment: {
    id: "fed0179d-8797-4fd6-90ed-b9a9b7dd10fa",
    defaultMessage: "Change Payment",
  },
  useExistingPayment: {
    id: "9f7b0414-453e-4742-9682-be9ef8233245",
    defaultMessage: "Use Existing Payment",
  },
  last4: {
    id: "f9042ece-eeb8-4674-8801-2b909061064e",
    defaultMessage: "Last 4",
  },
  accountName: {
    id: "30c9c95c-e800-4e25-8688-df1b1cc1b329",
    defaultMessage: "Account Name",
  },
});

// a new payment ui is being designed that will look similar to PaymentModalDetails and replace normal PaymentDetails (../details.js)
class PaymentModalDetails extends PureComponent {
  state = {
    cardNumberComplete: false,
    cardExpiryComplete: false,
    cardCvcComplete: false,
  };

  submitAch = (achData) => {
    const { change, onSubmit } = this.props;

    change("paymentSource", PAYMENT_ACH);
    change("achAccount", achData);
    // HACK: force onSubmit to happen after redux form handles change action
    // https://github.com/erikras/redux-form/issues/2818
    setTimeout(onSubmit, 0);
  };

  submitCard = () => {
    const { change, onSubmit } = this.props;

    change("paymentSource", PAYMENT_CARD);
    // HACK: force onSubmit to happen after redux form handles change action
    // https://github.com/erikras/redux-form/issues/2818
    setTimeout(onSubmit, 0);
  };

  connectBank = () => {
    verifyAch(this.submitAch);
  };

  collectPayment = () => {
    this.props.change("collectPayment", true);
  };

  changeCardElement = (evt) => {
    this.props.change(evt.elementType, evt.empty ? "" : "not empty");
    switch (evt.elementType) {
      case "cardNumber":
        this.setState({ cardNumberComplete: evt.complete });
        break;
      case "cardExpiry":
        this.setState({ cardExpiryComplete: evt.complete });
        break;
      case "cardCvc":
        this.setState({ cardCvcComplete: evt.complete });
        break;
      default:
        return null;
    }
  };

  render() {
    const {
      submitting,
      formValues,
      showSubscriptionTos,
      showDelayedChargeMessage,
      currentPaymentSource,
      onSubmit,
      submitButtonLabel,
      intl,
      touch,
    } = this.props;
    const { paymentSource, collectPayment } = formValues;
    const { cardNumberComplete, cardExpiryComplete, cardCvcComplete } = this.state;
    const cardComplete = cardNumberComplete && cardExpiryComplete && cardCvcComplete;

    return (
      <div className="PaymentModalDetails">
        {showDelayedChargeMessage && (
          <div
            data-automation-id="payment-delayed-charge-message"
            className="PaymentModalDetails--delayed-charge-message"
          >
            {intl.formatMessage(messages.delayedCharge)}
          </div>
        )}

        {currentPaymentSource && !collectPayment && !(submitting && collectPayment) && (
          <>
            <FormRow>
              <StyledTextInput
                automationId="current-payment-name"
                value={currentPaymentSource.name}
                label={
                  paymentSource === PAYMENT_CARD
                    ? intl.formatMessage(messages.cardName)
                    : intl.formatMessage(messages.accountName)
                }
                disabled
              />
            </FormRow>
            <FormRow>
              <StyledTextInput
                automationId="current-payment-last4"
                value={currentPaymentSource.last4}
                label={intl.formatMessage(messages.last4)}
                disabled
              />
            </FormRow>
          </>
        )}

        {collectPayment && getStripeCardElementsUI((evt) => this.changeCardElement(evt), touch)}

        {showSubscriptionTos && (
          <div
            data-automation-id="payment-subscription-tos"
            className="PaymentModalDetails--subscription-tos"
          >
            <TosV2 actionText={intl.formatMessage(messages.tosActionNew)} />
          </div>
        )}

        <div>
          {collectPayment ? (
            <>
              <Button
                automationId="payment-submit-card"
                className="PaymentModalDetails--button"
                onClick={this.submitCard}
                isLoading={submitting && paymentSource === PAYMENT_CARD}
                disabled={(submitting && paymentSource === PAYMENT_ACH) || !cardComplete}
                buttonColor="action"
                buttonSize="large"
                variant="primary"
                fullwidth
              >
                {submitButtonLabel || intl.formatMessage(messages.submit)}
              </Button>
              <Button
                automationId="payment-submit-ach"
                className="PaymentModalDetails--button"
                onClick={this.connectBank}
                isLoading={submitting && paymentSource === PAYMENT_ACH}
                disabled={submitting && paymentSource === PAYMENT_CARD}
                buttonColor="action"
                buttonSize="large"
                variant="secondary"
                fullwidth
              >
                {intl.formatMessage(messages.connectBank)}
              </Button>
              <FormGroupErrors fields={["achAccount"]} />
            </>
          ) : (
            <>
              <Button
                automationId="payment-use-existing"
                className="PaymentModalDetails--button"
                onClick={onSubmit}
                isLoading={submitting}
                buttonColor="action"
                buttonSize="large"
                variant="primary"
                fullwidth
              >
                {intl.formatMessage(messages.useExistingPayment)}
              </Button>
              <Button
                automationId="payment-change"
                className="PaymentModalDetails--button"
                onClick={this.collectPayment}
                disabled={submitting}
                buttonColor="action"
                buttonSize="large"
                variant="secondary"
                fullwidth
              >
                {intl.formatMessage(messages.changePayment)}
              </Button>
            </>
          )}
        </div>
      </div>
    );
  }
}

PaymentModalDetails.propTypes = {
  change: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  showSubscriptionTos: PropTypes.bool.isRequired,
  showDelayedChargeMessage: PropTypes.bool.isRequired,
  currentPaymentSource: PropTypes.shape({
    last4: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
  }),
  submitButtonLabel: PropTypes.node,

  // react-intl
  intl: PropTypes.object.isRequired,

  // getFormValues
  formValues: PropTypes.object.isRequired,
};

PaymentModalDetails.defaultProps = {
  currentPaymentSource: null,
  submitButtonLabel: null,
};

export default compose(getFormValues("payment"), injectIntl)(PaymentModalDetails);
